/* Peachy — product page: variant picker, gallery, bundle tiers, sticky bar, recently viewed */

(function () {
  'use strict';

  const P = window.Peachy || {};
  const define = P.define || ((n, c) => (customElements.get(n) ? null : customElements.define(n, c)));

  /* ---------------- variant picker ---------------- */

  define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        /* The element ids are namespaced per instance, but the Section Rendering API
           needs the REAL section id — they differ for the featured-product section. */
        this.renderSectionId = this.dataset.renderSection || this.dataset.section;
        this.productUrl = this.dataset.url;
        this.updateUrl = this.dataset.updateUrl !== 'false';
        this.variants = this.readVariants();
        this.addEventListener('change', () => this.onVariantChange());
      }

      readVariants() {
        const script = this.querySelector('[type="application/json"]');
        if (!script) return [];
        try {
          return JSON.parse(script.textContent);
        } catch (e) {
          return [];
        }
      }

      get selectedOptions() {
        return Array.from(this.querySelectorAll('input[type="radio"]:checked, select')).map((el) =>
          el.tagName === 'SELECT' ? el.value : el.value
        );
      }

      onVariantChange() {
        const selected = this.selectedOptions;
        this.updateOptionLabels();
        const variant = this.variants.find((v) =>
          v.options.every((option, index) => option === selected[index])
        );
        this.currentVariant = variant;
        this.setAvailability(selected);

        if (!variant) {
          this.publish(null);
          return;
        }
        if (this.updateUrl && variant.id) {
          window.history.replaceState({}, '', `${this.productUrl}?variant=${variant.id}`);
        }
        this.publish(variant);
      }

      updateOptionLabels() {
        for (const group of this.querySelectorAll('[data-option-index]')) {
          const output = group.querySelector('[data-option-value]');
          const checked = group.querySelector('input[type="radio"]:checked');
          if (output && checked) output.textContent = checked.value;
        }
      }

      /* Grey out option values that cannot combine with the current selection */
      setAvailability(selected) {
        const groups = Array.from(this.querySelectorAll('[data-option-index]'));
        for (const [groupIndex, group] of groups.entries()) {
          for (const input of group.querySelectorAll('input[type="radio"]')) {
            const candidate = selected.slice();
            candidate[groupIndex] = input.value;
            const match = this.variants.find((v) =>
              v.options.every((option, i) =>
                i === groupIndex ? option === input.value : option === candidate[i]
              )
            );
            const available = Boolean(match?.available);
            input.classList.toggle('is-unavailable', !available);
            input.nextElementSibling?.classList.toggle('is-unavailable', !available);
          }
        }
      }

      publish(variant) {
        document.dispatchEvent(
          new CustomEvent('peachy:variant:change', {
            detail: { variant, sectionId: this.sectionId, html: null }
          })
        );
        this.renderState(variant);
      }

      /* Fetch the freshly rendered buy box for accurate price/stock markup */
      renderState(variant) {
        const scope = document.getElementById(`ProductInfo-${this.sectionId}`);
        const button = document.querySelector(`[data-atc="${this.sectionId}"]`);

        if (!variant) {
          if (button) {
            button.setAttribute('aria-disabled', 'true');
            button.disabled = true;
            const label = button.querySelector('.btn__label');
            if (label) label.textContent = P.strings.unavailable || 'Unavailable';
          }
          return;
        }

        if (button) {
          button.disabled = !variant.available;
          button.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
          const label = button.querySelector('.btn__label');
          if (label) {
            label.textContent = variant.available
              ? P.strings.addToCart || 'Add to basket'
              : P.strings.soldOut || 'Sold out';
          }
        }

        const idInput = document.querySelector(`[data-variant-id="${this.sectionId}"]`);
        if (idInput) idInput.value = variant.id;

        if (!scope || !this.productUrl) return;
        this.renderVariant(variant, scope);
      }

      /* Pull the freshly rendered blocks for this variant. Aborts any in-flight
         request so quick successive clicks can't paint a stale price. */
      async renderVariant(variant, scope) {
        this.controller?.abort();
        this.controller = new AbortController();

        const url = new URL(this.productUrl, window.location.origin);
        url.searchParams.set('variant', variant.id);
        url.searchParams.set('section_id', this.renderSectionId);

        try {
          const response = await fetch(url, { signal: this.controller.signal });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const text = await response.text();
          const parsed = new DOMParser().parseFromString(text, 'text/html');

          const selectors = [
            '[data-price-block]',
            '[data-stock-block]',
            '[data-sku-block]',
            '[data-tiers-block]'
          ];
          for (const selector of selectors) {
            const source = parsed.querySelector(`#ProductInfo-${this.sectionId} ${selector}`);
            const target = scope.querySelector(selector);
            if (!source || !target) continue;

            /* The tier block IS a custom element. Replacing its innerHTML would
               strip the listeners bound in connectedCallback and leave the tiers
               dead; swapping the element itself re-upgrades it. */
            if (selector === '[data-tiers-block]') {
              target.replaceWith(document.importNode(source, true));
            } else {
              target.innerHTML = source.innerHTML;
            }
          }

          if (variant.featured_media_id) this.showMedia(variant.featured_media_id);
          document.dispatchEvent(new CustomEvent('peachy:variant:rendered', { detail: { variant } }));
        } catch (error) {
          /* AbortError or network failure — the optimistic state already applied stands */
        }
      }

      disconnectedCallback() {
        this.controller?.abort();
      }

      showMedia(mediaId) {
        const gallery = document.querySelector(`product-gallery[data-section="${this.sectionId}"]`);
        if (gallery && typeof gallery.selectMedia === 'function') gallery.selectMedia(mediaId);
      }
    }
  );

  /* ---------------- gallery ---------------- */

  define(
    'product-gallery',
    class ProductGallery extends HTMLElement {
      connectedCallback() {
        this.slidesWrap = this.querySelector('[data-gallery-slides]');
        this.slides = Array.from(this.querySelectorAll('[data-media-id]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-thumb-media]'));

        for (const thumb of this.thumbs) {
          thumb.addEventListener('click', (event) => {
            event.preventDefault();
            this.selectMedia(thumb.dataset.thumbMedia, true);
          });
        }

        const prev = this.querySelector('[data-gallery-prev]');
        const next = this.querySelector('[data-gallery-next]');
        if (prev) prev.addEventListener('click', () => this.step(-1));
        if (next) next.addEventListener('click', () => this.step(1));

        if (this.slidesWrap) {
          this.slidesWrap.addEventListener(
            'scroll',
            P.debounce(() => this.syncFromScroll(), 120)
          );
        }
      }

      get currentIndex() {
        if (!this.slidesWrap) return 0;
        const width = this.slidesWrap.clientWidth || 1;
        return Math.round(this.slidesWrap.scrollLeft / width);
      }

      step(direction) {
        const index = Math.min(Math.max(this.currentIndex + direction, 0), this.slides.length - 1);
        const slide = this.slides[index];
        if (slide) this.selectMedia(slide.dataset.mediaId, true);
      }

      syncFromScroll() {
        const slide = this.slides[this.currentIndex];
        if (slide) this.markActive(slide.dataset.mediaId);
      }

      selectMedia(mediaId, focusStage) {
        const slide = this.slides.find((el) => el.dataset.mediaId === String(mediaId));
        if (!slide || !this.slidesWrap) return;
        this.slidesWrap.scrollTo({ left: slide.offsetLeft - this.slidesWrap.offsetLeft, behavior: 'smooth' });
        this.markActive(mediaId);
        if (focusStage) {
          const media = slide.querySelector('img, video, iframe, model-viewer');
          if (media) media.setAttribute('tabindex', '-1');
        }
      }

      markActive(mediaId) {
        for (const thumb of this.thumbs) {
          thumb.setAttribute(
            'aria-current',
            thumb.dataset.thumbMedia === String(mediaId) ? 'true' : 'false'
          );
        }
      }
    }
  );

  /* ---------------- bundle tiers ---------------- */

  define(
    'bundle-tiers',
    class BundleTiers extends HTMLElement {
      connectedCallback() {
        this.inputs = Array.from(this.querySelectorAll('input[type="radio"]'));
        if (!this.inputs.length) return;
        this.quantityInput = document.querySelector(`[data-quantity-target="${this.dataset.section}"]`);
        this.variantInput = document.querySelector(`[data-variant-id="${this.dataset.section}"]`);
        for (const input of this.inputs) {
          input.addEventListener('change', () => this.apply(input));
        }
        const checked = this.inputs.find((i) => i.checked) || this.inputs[0];
        if (checked) {
          checked.checked = true;
          this.apply(checked, true);
        }
      }

      apply(input, silent) {
        for (const tier of this.querySelectorAll('[data-tier]')) {
          tier.classList.toggle('is-selected', tier.contains(input));
        }

        if (this.dataset.mode === 'variant') {
          if (this.variantInput && input.dataset.variantId) this.variantInput.value = input.dataset.variantId;
        } else if (input.dataset.quantity) {
          /* Every quantity target, not just the buy box — the sticky bar has one
             too, and a shopper who picks "3 units" then buys from the sticky bar
             must not silently get one. */
          for (const target of document.querySelectorAll(
            `[data-quantity-target="${this.dataset.section}"]`
          )) {
            target.value = input.dataset.quantity;
            target.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        /* Gift mode: hand the chosen tier's free-gift variants to every form that
           can submit for this section, so the gifts arrive as real line items. */
        for (const target of document.querySelectorAll(`[data-gifts-target="${this.dataset.section}"]`)) {
          target.value = input.dataset.gifts || '';
        }

        if (!silent) {
          document.dispatchEvent(
            new CustomEvent('peachy:tier:change', {
              detail: { quantity: input.dataset.quantity, gifts: input.dataset.gifts || '' }
            })
          );
        }
      }
    }
  );

  /* ---------------- sticky add to cart ---------------- */

  define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        const anchorId = this.dataset.watch;
        const anchor = anchorId ? document.getElementById(anchorId) : null;
        if (!anchor) return;
        this.observer = new IntersectionObserver(
          ([entry]) => {
            const passed = entry.boundingClientRect.top < 0 && !entry.isIntersecting;
            this.classList.toggle('is-visible', passed);
          },
          { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
        );
        this.observer.observe(anchor);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }
    }
  );


  /* ---------------- recommendations loader ---------------- */

  define(
    'product-recommendations',
    class ProductRecommendations extends HTMLElement {
      connectedCallback() {
        this.load();
      }

      async load() {
        const url = this.dataset.url;
        if (!url) return;
        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error(String(response.status));
          const text = await response.text();
          const parsed = new DOMParser().parseFromString(text, 'text/html');
          const fresh = parsed.querySelector('product-recommendations');
          if (fresh && fresh.innerHTML.trim().length) {
            this.innerHTML = fresh.innerHTML;
          } else {
            this.hidden = true;
          }
        } catch (error) {
          this.hidden = true;
        }
      }
    }
  );

  /* ---------------- recently viewed ---------------- */

  const RV_KEY = 'peachy:recently-viewed';

  function readRecentlyViewed() {
    try {
      return JSON.parse(localStorage.getItem(RV_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  P.trackRecentlyViewed = function (handle) {
    if (!handle) return;
    try {
      const list = readRecentlyViewed().filter((h) => h !== handle);
      list.unshift(handle);
      localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 12)));
    } catch (e) {
      /* private mode */
    }
  };

  define(
    'recently-viewed',
    class RecentlyViewed extends HTMLElement {
      connectedCallback() {
        const exclude = this.dataset.exclude;
        const handles = readRecentlyViewed()
          .filter((handle) => handle !== exclude)
          .slice(0, parseInt(this.dataset.count || '4', 10));
        if (!handles.length) {
          this.hidden = true;
          return;
        }
        const query = handles.map((handle) => `handle:${handle}`).join(' OR ');
        const url = new URL(P.routes.search || `${P.routes.root}search`, window.location.origin);
        url.searchParams.set('type', 'product');
        url.searchParams.set('q', query);
        url.searchParams.set('section_id', this.dataset.section);
        fetch(url)
          .then((response) => response.text())
          .then((text) => {
            const parsed = new DOMParser().parseFromString(text, 'text/html');
            const results = parsed.querySelector('[data-recently-viewed-results]');
            const target = this.querySelector('[data-recently-viewed-results]');
            if (results && target && results.children.length) {
              target.innerHTML = results.innerHTML;
              this.hidden = false;
            } else {
              this.hidden = true;
            }
          })
          .catch(() => {
            this.hidden = true;
          });
      }
    }
  );
})();
